import React, { useMemo } from 'react';
import { Button } from 'antd';
import type { TProviderActionGroup, TCredentialingActionItem } from 'types/credentialing';
import { SvgAlert } from '../SvgIcons';
import ActionNeededCard from '../ActionNeededCard';
import { getPaymentActionCopy } from '../../statusMeta';
import s from './s.module.less';

interface Props {
    groupActionNeeded: TProviderActionGroup[];
    onUpdateProfile?: (item: TCredentialingActionItem, providerId: number) => void;
    onFixPayment?: (item: TCredentialingActionItem) => void;
    onConfirmSignature?: (item: TCredentialingActionItem, credentialingPayerId: number) => void;
}

interface ProviderActions {
    providerId: number;
    providerName: string;
    paymentItems: (TCredentialingActionItem & { payerName: string })[];
    signatureItems: { payerName: string; credentialingPayerId: number; item: TCredentialingActionItem }[];
    docRfiItems: { payer: string; reason: string; actionItem: TCredentialingActionItem }[];
}

const buildProviderActions = (group: TProviderActionGroup): ProviderActions => {
    const name = `${group.firstName || ''} ${group.lastName || ''}`.trim();
    const seenRefIds = new Set<number>();

    const paymentItems = group.panels.flatMap((p) =>
        p.actionItems.filter((it) => it.type === 'PAYMENT').map((it) => ({ ...it, payerName: p.payerName })),
    );

    const signatureItems = group.panels
        .filter((p) => p.actionItems.some((it) => it.type === 'SIGNATURE'))
        .map((p) => ({
            payerName: p.payerName,
            credentialingPayerId: p.credentialingPayerId,
            item: p.actionItems.find((it) => it.type === 'SIGNATURE')!,
        }));

    const docRfiItems: ProviderActions['docRfiItems'] = [];
    group.panels.forEach((p) => {
        p.actionItems.forEach((it) => {
            if (it.type !== 'DOCUMENT' && it.type !== 'RFI') return;
            if (it.refId != null && seenRefIds.has(it.refId)) return;
            if (it.refId != null) seenRefIds.add(it.refId);
            docRfiItems.push({ payer: p.payerName, reason: it.reason || 'Action required', actionItem: it });
        });
    });

    return { providerId: group.providerId, providerName: name, paymentItems, signatureItems, docRfiItems };
};

const GroupActionNeededBanner = ({ groupActionNeeded, onUpdateProfile, onFixPayment, onConfirmSignature }: Props) => {
    const providerActionsList = useMemo(
        () =>
            groupActionNeeded
                .map(buildProviderActions)
                .filter(
                    (pa) => pa.paymentItems.length > 0 || pa.signatureItems.length > 0 || pa.docRfiItems.length > 0,
                ),
        [groupActionNeeded],
    );

    if (providerActionsList.length === 0) return null;

    return (
        <div className={s.wrap}>
            {providerActionsList.map((pa) => (
                <div key={pa.providerId} className={s.providerSection}>
                    <div className={s.providerName}>{pa.providerName}</div>

                    {pa.paymentItems.length > 0 &&
                        (() => {
                            const payment = getPaymentActionCopy(pa.paymentItems);
                            return (
                                <ActionNeededCard
                                    name={payment.name}
                                    detail={payment.detail}
                                    btnText={payment.btnText}
                                    onAction={() => onFixPayment?.(payment.item)}
                                />
                            );
                        })()}

                    {pa.signatureItems.map((sig) => (
                        <ActionNeededCard
                            key={sig.credentialingPayerId}
                            name={`${sig.payerName} approved you`}
                            detail={`${sig.payerName} emailed a contract to your inbox. Sign it, then mark it as signed here to move on to billing setup.`}
                            btnText="I've signed it"
                            onAction={() => onConfirmSignature?.(sig.item, sig.credentialingPayerId)}
                        />
                    ))}

                    {pa.docRfiItems.length > 0 && (
                        <div className={s.docRfiCard}>
                            <div className={s.docRfiHeader}>
                                <div className={s.iconWrap}>
                                    <SvgAlert />
                                </div>
                                <div className={s.docRfiHeaderBody}>
                                    <div className={s.titleText}>Documents needed</div>
                                    <div className={s.subtitleText}>
                                        {pa.docRfiItems.length} item{pa.docRfiItems.length > 1 ? 's' : ''} need updating
                                    </div>
                                </div>
                            </div>
                            {pa.docRfiItems.map((it, idx) => (
                                <div key={idx} className={`${s.docRfiRow}${idx > 0 ? ` ${s.withBorder}` : ''}`}>
                                    <div className={s.docRfiBody}>
                                        <span className={s.docRfiPayer}>{it.payer}</span>
                                        <span className={s.docRfiReason}>{it.reason}</span>
                                    </div>
                                    <Button
                                        type="primary"
                                        className={s.updateBtn}
                                        onClick={() => onUpdateProfile?.(it.actionItem, pa.providerId)}
                                    >
                                        Update
                                    </Button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
};

export default GroupActionNeededBanner;
